/**
 * PriorityBucketCard — clickable Section-1 card in the cockpit.
 *
 * Severities map to colour rails (red/orange/yellow) matching the
 * operator brief's "🔴/🟠/🟡" buckets. Active state = currently the
 * smart-queue filter, so clicking the bucket and clicking its mirror
 * chip lead to the same UI state.
 */
import React from 'react';

const SEVERITY_THEME = {
  critical: {
    rail: 'bg-red-500',
    countText: 'text-red-600',
    activeBg: 'bg-red-50 border-red-300',
    icon: '🔴',
  },
  warning: {
    rail: 'bg-orange-500',
    countText: 'text-orange-600',
    activeBg: 'bg-orange-50 border-orange-300',
    icon: '🟠',
  },
  info: {
    rail: 'bg-yellow-500',
    countText: 'text-yellow-600',
    activeBg: 'bg-yellow-50 border-yellow-300',
    icon: '🟡',
  },
};

export const PriorityBucketCard = ({ bucket, isActive, loading, onClick }) => {
  if (loading || !bucket) {
    return (
      <div
        className="rounded-xl bg-white border border-[#E5E7EB] shadow-sm p-4 min-h-[112px] animate-pulse"
        aria-hidden="true"
      >
        <div className="h-3 bg-gray-200 rounded w-2/3 mb-3" />
        <div className="h-7 bg-gray-200 rounded w-1/3" />
      </div>
    );
  }

  const theme = SEVERITY_THEME[bucket.severity] || SEVERITY_THEME.info;
  const hasItems = bucket.count > 0;

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`bucket-card-${bucket.id}`}
      aria-pressed={isActive}
      className={`relative text-left rounded-xl border-2 shadow-sm hover:shadow-md transition-all overflow-hidden min-h-[112px] flex flex-col justify-between ${
        isActive
          ? `${theme.activeBg} ring-2 ring-[#2F5D3A]/30`
          : hasItems
            ? 'bg-white border-[#E5E7EB] hover:border-[#6FBF73]'
            : 'bg-white border-[#E5E7EB] opacity-75 hover:opacity-100'
      }`}
    >
      <span className={`absolute top-0 left-0 right-0 h-1 ${theme.rail}`} aria-hidden="true" />
      <div className="px-4 pt-5 pb-2 flex-1 flex flex-col">
        <div className="flex items-start justify-between gap-2">
          <span className="text-[10px] uppercase tracking-wider text-[#1F3D2C]/60 font-bold leading-tight">
            <span className="mr-1">{theme.icon}</span>
            {bucket.label}
          </span>
        </div>
        <div className="mt-auto pt-2 flex items-end gap-2">
          <span
            className={`text-3xl font-black leading-none ${
              hasItems ? theme.countText : 'text-gray-300'
            }`}
            data-testid={`bucket-card-count-${bucket.id}`}
          >
            {bucket.count}
          </span>
          <span className="text-xs text-[#1F3D2C]/55 pb-1">
            {bucket.count === 1 ? 'order' : 'orders'}
          </span>
        </div>
      </div>
    </button>
  );
};

export default PriorityBucketCard;
